"use client";

import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { cn } from "utils/utils";
import { clientLogger } from "modules/common/logger/client";
import { ErrorSupportInfo } from "modules/common/components/ErrorSupportInfo";

interface MapErrorBoundaryProps {
  children: ReactNode;
  className?: string;
}

interface MapErrorBoundaryState {
  hasError: boolean;
}

export class MapErrorBoundary extends Component<
  MapErrorBoundaryProps,
  MapErrorBoundaryState
> {
  state: MapErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): MapErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    clientLogger.error("MapErrorBoundary", "Map crashed", {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={cn(
            "rounded-box relative z-0 flex w-full items-center justify-center p-4",
            this.props.className
          )}
        >
          <div className="card bg-base-200 w-full max-w-md shadow-lg">
            <div className="card-body items-center text-center">
              <h2 className="card-title text-error">Map unavailable</h2>
              <p className="text-base-content/70 text-sm">
                The map failed to load. This may be due to browser compatibility
                or a temporary issue.
              </p>
              <div className="card-actions mt-2">
                <button className="btn btn-primary" onClick={this.handleRetry}>
                  Try Again
                </button>
              </div>
              <ErrorSupportInfo className="mt-4" />
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
